import InternalAdminLayout from '@/layouts/InternalAdminLayout';
import { BASE_INTERNAL_URL } from '@/router/constants';
import type { RouteData } from '@/router/routes';
import { lazyWithReload } from '@/router/utils';
import Error from '@/routes/Error';

const InternalAdminAiLessonGenerator = lazyWithReload(
  () => import('@/routes/internalAdminApp/InternalAdminAiLessonGenerator')
);
const InternalAdminActivityBank = lazyWithReload(
  () => import('@/routes/internalAdminApp/InternalAdminActivityBank/InternalAdminActivityBank')
);
const InternalAdminActivityBankSimpleQuiz = lazyWithReload(
  () => import('@/routes/internalAdminApp/InternalAdminActivityBankSimpleQuiz')
);
const InternalAdminAssetUpload = lazyWithReload(
  () => import('@/routes/internalAdminApp/InternalAdminAssetUpload/InternalAdminAssetUpload')
);
const InternalAdminActivityPreview = lazyWithReload(
  () => import('@/routes/internalAdminApp/InternalAdminActivityPreview')
);
const InternalAdminBulkImport = lazyWithReload(() => import('@/routes/internalAdminApp/InternalAdminBulkImport'));
const InternalAdminBulkImportV2 = lazyWithReload(() => import('@/routes/internalAdminApp/InternalAdminBulkImportV2'));
const InternalAdminClassroomDetail = lazyWithReload(
  () => import('@/routes/internalAdminApp/InternalAdminClassroomDetail')
);
const InternalAdminClassroomReorderLessons = lazyWithReload(
  () => import('@/routes/internalAdminApp/InternalAdminClassroomReorderLessons')
);
const InternalAdminDataExports = lazyWithReload(() => import('@/routes/internalAdminApp/InternalAdminDataExports'));
const InternalAdminFeatureFlags = lazyWithReload(() => import('@/routes/internalAdminApp/InternalAdminFeatureFlags'));
const InternalAdminLessonCreate = lazyWithReload(() => import('@/routes/internalAdminApp/InternalAdminLessonCreate'));
const InternalAdminLessonDetail = lazyWithReload(() => import('@/routes/internalAdminApp/InternalAdminLessonDetail'));
const InternalAdminLessonImportCSV = lazyWithReload(
  () => import('@/routes/internalAdminApp/InternalAdminLessonImportCSV')
);
const InternalAdminLogin = lazyWithReload(() => import('@/routes/internalAdminApp/InternalAdminLogin'));
const InternalAdminModularLessonCreate = lazyWithReload(
  () => import('@/routes/internalAdminApp/InternalAdminModularLessonCreate')
);
const InternalAdminSchoolDetail = lazyWithReload(() => import('@/routes/internalAdminApp/InternalAdminSchoolDetail'));
const InternalAdminEditSchoolDetailContainer = lazyWithReload(() =>
  import('@/routes/internalAdminApp/InternalAdminSchoolDetail/InternalAdminEditSchoolDetail.container').then((m) => ({
    default: m.InternalAdminEditSchoolDetailContainer,
  }))
);
const InternalAdminSchoolDetailReport = lazyWithReload(
  () => import('@/routes/internalAdminApp/InternalAdminSchoolDetail/InternalAdminSchoolDetailReport')
);
const InternalAdminSchoolDetailIndividualReport = lazyWithReload(
  () => import('@/routes/internalAdminApp/InternalAdminSchoolDetail/InternalAdminSchoolDetailIndividualReport')
);
const InternalAdminSchools = lazyWithReload(() => import('@/routes/internalAdminApp/InternalAdminSchools'));
const InternalAdminUserDetail = lazyWithReload(() => import('@/routes/internalAdminApp/InternalAdminUserDetail'));
const InternalAdminUserLessonDetail = lazyWithReload(
  () => import('@/routes/internalAdminApp/InternalAdminUserLessonDetail')
);
const InternalAdminUsers = lazyWithReload(() => import('@/routes/internalAdminApp/InternalAdminUsers'));
const InternalAdminVocabSearchRedirect = lazyWithReload(
  () => import('@/routes/internalAdminApp/InternalAdminVocabSearchRedirect')
);
const InternalAdminVocabLibrary = lazyWithReload(() => import('@/routes/internalAdminApp/InternalAdminVocabLibrary'));
const InternalAdminVocabEntryEdit = lazyWithReload(
  () => import('@/routes/internalAdminApp/InternalAdminVocabEntryEdit')
);
const InternalAdminVocabEntryAdd = lazyWithReload(() => import('@/routes/internalAdminApp/InternalAdminVocabEntryAdd'));
const InternalAdminHomeRedirect = lazyWithReload(() => import('@/routes/internalAdminApp/InternalAdminHomeRedirect'));
const CreateEdetAccessCode = lazyWithReload(
  () =>
    import('@/routes/internalAdminApp/InternalAdminClassroomDetail/InternalAdminClassroomDetailEdEtCode/CreateEdetAccessCode')
);
const InternalAdminContentBuilder = lazyWithReload(
  () => import('@/routes/internalAdminApp/InternalAdminContentBuilder')
);
const InternalAdminItems = lazyWithReload(() => import('@/routes/internalAdminApp/InternalAdminItems'));
const InternalAdminLessonBlueprints = lazyWithReload(
  () => import('@/routes/internalAdminApp/InternalAdminLessonBlueprints')
);
const InternalAdminLessonBlueprintDetail = lazyWithReload(
  () => import('@/routes/internalAdminApp/InternalAdminLessonBlueprintDetail')
);

enum InternalAdminRouteKeys {
  Home = 'Home',
  Login = 'Login',
  Users = 'Users',
  UserDetail = 'UserDetail',
  UserLessonDetail = 'UserLessonDetail',
  Schools = 'Schools',
  SchoolDetail = 'SchoolDetail',
  EditSchoolDetail = 'EditSchoolDetail',
  SchoolDetailReport = 'SchoolDetailReport',
  SchoolDetailIndividualReport = 'SchoolDetailIndividualReport',
  ClassroomDetail = 'ClassroomDetail',
  ClassroomReorderLessons = 'ClassroomReorderLessons',
  ClassroomLessons = 'ClassroomLessons',
  ClassroomLessonCreate = 'ClassroomLessonCreate',
  ClassroomLessonImportCSV = 'ClassroomLessonImportCSV',
  ClassroomModularLessonCreate = 'ClassroomModularLessonCreate',
  ClassroomUsers = 'ClassroomUsers',
  ClassroomAccessCodes = 'ClassroomAccessCodes',
  LessonDetail = 'LessonDetail',
  VocabSearchRedirect = 'VocabSearchRedirect',
  VocabLibrary = 'VocabLibrary',
  VocabEntryAdd = 'VocabEntryAdd',
  VocabEntryEdit = 'VocabEntryEdit',
  FeatureFlags = 'FeatureFlags',
  DataExports = 'DataExports',
  BulkImport = 'BulkImport',
  BulkImportV2 = 'BulkImportV2',
  ActivityBank = 'ActivityBank',
  ActivityBankSimpleQuiz = 'ActivityBankSimpleQuiz',
  AssetUpload = 'AssetUpload',
  ActivityPreview = 'ActivityPreview',
  ContentBuilder = 'ContentBuilder',
  CreateEdetAccessCode = 'CreateEdetAccessCode',
  Items = 'Items',
  AiLessonGenerator = 'AiLessonGenerator',
  LessonBlueprints = 'LessonBlueprints',
  LessonBlueprintDetail = 'LessonBlueprintDetail',
}

const internalAdminRoutes = {
  [InternalAdminRouteKeys.Home]: {
    // No dashboard — the root redirects to Schools (see InternalAdminHomeRedirect).
    path: ``,
    component: InternalAdminHomeRedirect,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: () => {
      return `/${BASE_INTERNAL_URL}`;
    },
  },
  [InternalAdminRouteKeys.Login]: {
    path: `login`, // This is a public routes, therefore, it won't be inside the InternalAdmin Route and need its own complete path
    component: InternalAdminLogin,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: () => {
      return `/${BASE_INTERNAL_URL}/login`;
    },
  },
  [InternalAdminRouteKeys.Users]: {
    path: `users`,
    component: InternalAdminUsers,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: () => {
      return `/${BASE_INTERNAL_URL}/users`;
    },
  },
  [InternalAdminRouteKeys.UserDetail]: {
    path: `users/:userId`,
    component: InternalAdminUserDetail,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: ({ userId }: { userId: string }) => {
      return `/${BASE_INTERNAL_URL}/users/${userId}`;
    },
  },
  [InternalAdminRouteKeys.UserLessonDetail]: {
    path: `users/:userId/lesson/:lessonId/lessonInstance/:lessonInstanceId`,
    component: InternalAdminUserLessonDetail,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: ({
      userId,
      lessonId,
      lessonInstanceId,
    }: {
      userId: string;
      lessonId: string;
      lessonInstanceId: string;
    }) => {
      return `/${BASE_INTERNAL_URL}/users/${userId}/lesson/${lessonId}/lessonInstance/${lessonInstanceId}`;
    },
  },
  [InternalAdminRouteKeys.VocabSearchRedirect]: {
    // Vocab Search was removed (LX-993 follow-up): lookup lives in Vocab
    // Library, and the page's nav slot became Duplicate Check. Old links and
    // bookmarks land in the Library.
    path: `vocab-search`,
    component: InternalAdminVocabSearchRedirect,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: () => {
      return `/${BASE_INTERNAL_URL}/vocab-search`;
    },
  },
  [InternalAdminRouteKeys.VocabLibrary]: {
    path: `vocab-library`,
    component: InternalAdminVocabLibrary,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: () => {
      return `/${BASE_INTERNAL_URL}/vocab-library`;
    },
  },
  [InternalAdminRouteKeys.VocabEntryAdd]: {
    // Static `new` segment — ranks above `:senseId/edit`, so it can never be
    // swallowed by the edit route's param.
    path: `vocab-library/new`,
    component: InternalAdminVocabEntryAdd,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: () => {
      return `/${BASE_INTERNAL_URL}/vocab-library/new`;
    },
  },
  [InternalAdminRouteKeys.VocabEntryEdit]: {
    path: `vocab-library/:senseId/edit`,
    component: InternalAdminVocabEntryEdit,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: ({ senseId }: { senseId: string }) => {
      return `/${BASE_INTERNAL_URL}/vocab-library/${senseId}/edit`;
    },
  },
  [InternalAdminRouteKeys.FeatureFlags]: {
    path: `feature-flags`,
    component: InternalAdminFeatureFlags,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: () => {
      return `/${BASE_INTERNAL_URL}/feature-flags`;
    },
  },
  [InternalAdminRouteKeys.Schools]: {
    path: `schools`,
    component: InternalAdminSchools,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: () => {
      return `/${BASE_INTERNAL_URL}/schools`;
    },
  },
  [InternalAdminRouteKeys.SchoolDetail]: {
    path: `schools/:schoolId`,
    component: InternalAdminSchoolDetail,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: ({ schoolId }: { schoolId: string }) => {
      return `/${BASE_INTERNAL_URL}/schools/${schoolId}`;
    },
  },
  [InternalAdminRouteKeys.EditSchoolDetail]: {
    path: `schools/:schoolId/edit`,
    component: InternalAdminEditSchoolDetailContainer,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: ({ schoolId }: { schoolId: string }) => {
      return `/${BASE_INTERNAL_URL}/schools/${schoolId}/edit`;
    },
  },
  [InternalAdminRouteKeys.SchoolDetailReport]: {
    path: `schools/:schoolId/report`,
    component: InternalAdminSchoolDetailReport,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: ({ schoolId }: { schoolId: string }) => {
      return `/${BASE_INTERNAL_URL}/schools/${schoolId}/report`;
    },
  },
  [InternalAdminRouteKeys.SchoolDetailIndividualReport]: {
    path: `schools/:schoolId/individual-report`,
    component: InternalAdminSchoolDetailIndividualReport,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: ({ schoolId }: { schoolId: string }) => {
      return `/${BASE_INTERNAL_URL}/schools/${schoolId}/individual-report`;
    },
  },
  [InternalAdminRouteKeys.ClassroomDetail]: {
    path: `classrooms/:classroomId`,
    component: InternalAdminClassroomDetail,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: ({ classroomId }: { classroomId: string }) => {
      return `/${BASE_INTERNAL_URL}/classrooms/${classroomId}`;
    },
  },
  [InternalAdminRouteKeys.ClassroomReorderLessons]: {
    path: `classrooms/:classroomId/reorder-lessons`,
    component: InternalAdminClassroomReorderLessons,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: ({ classroomId }: { classroomId: string }) => {
      return `/${BASE_INTERNAL_URL}/classrooms/${classroomId}/reorder-lessons`;
    },
  },
  [InternalAdminRouteKeys.ClassroomLessons]: {
    path: `classrooms/:classroomId/lessons`,
    component: InternalAdminClassroomDetail,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: ({ classroomId }: { classroomId: string }) => {
      return `/${BASE_INTERNAL_URL}/classrooms/${classroomId}`;
    },
  },
  [InternalAdminRouteKeys.ClassroomLessonCreate]: {
    path: `classrooms/:classroomId/new-lesson`,
    component: InternalAdminLessonCreate,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: ({ classroomId }: { classroomId: string }) => {
      return `/${BASE_INTERNAL_URL}/classrooms/${classroomId}/new-lesson`;
    },
  },
  [InternalAdminRouteKeys.ClassroomLessonImportCSV]: {
    path: `classrooms/:classroomId/import-lesson`,
    component: InternalAdminLessonImportCSV,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: ({ classroomId }: { classroomId: string }) => {
      return `/${BASE_INTERNAL_URL}/classrooms/${classroomId}/import-lesson`;
    },
  },
  [InternalAdminRouteKeys.ClassroomModularLessonCreate]: {
    path: `classrooms/:classroomId/create-modular-lesson`,
    component: InternalAdminModularLessonCreate,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: ({ classroomId }: { classroomId: string }) => {
      return `/${BASE_INTERNAL_URL}/classrooms/${classroomId}/create-modular-lesson`;
    },
  },
  [InternalAdminRouteKeys.ClassroomUsers]: {
    path: `classrooms/:classroomId/users`,
    component: InternalAdminClassroomDetail,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: ({ classroomId }: { classroomId: string }) => {
      return `/${BASE_INTERNAL_URL}/classrooms/${classroomId}`;
    },
  },
  [InternalAdminRouteKeys.ClassroomAccessCodes]: {
    path: `classrooms/:classroomId/accesscodes`,
    component: InternalAdminClassroomDetail,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: ({ classroomId }: { classroomId: string }) => {
      return `/${BASE_INTERNAL_URL}/classrooms/${classroomId}`;
    },
  },
  [InternalAdminRouteKeys.LessonDetail]: {
    path: `classrooms/:classroomId/lessons/:lessonId`,
    component: InternalAdminLessonDetail,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: ({ classroomId, lessonId }: { classroomId: string; lessonId: string }) => {
      return `/${BASE_INTERNAL_URL}/classrooms/${classroomId}/lessons/${lessonId}`;
    },
  },
  [InternalAdminRouteKeys.DataExports]: {
    path: `data-exports`,
    component: InternalAdminDataExports,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: () => {
      return `/${BASE_INTERNAL_URL}/data-exports`;
    },
  },
  [InternalAdminRouteKeys.BulkImport]: {
    path: `bulk-import`,
    component: InternalAdminBulkImport,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: () => {
      return `/${BASE_INTERNAL_URL}/bulk-import`;
    },
  },
  [InternalAdminRouteKeys.BulkImportV2]: {
    path: `bulk-import-v2`,
    component: InternalAdminBulkImportV2,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: () => {
      return `/${BASE_INTERNAL_URL}/bulk-import-v2`;
    },
  },
  [InternalAdminRouteKeys.ActivityBank]: {
    path: `activity-bank`,
    component: InternalAdminActivityBank,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: () => {
      return `/${BASE_INTERNAL_URL}/activity-bank`;
    },
  },
  [InternalAdminRouteKeys.ActivityBankSimpleQuiz]: {
    path: `activity-bank/simple-quiz`,
    component: InternalAdminActivityBankSimpleQuiz,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: () => {
      return `/${BASE_INTERNAL_URL}/activity-bank/simple-quiz`;
    },
  },
  [InternalAdminRouteKeys.AssetUpload]: {
    path: `asset-upload`,
    component: InternalAdminAssetUpload,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: () => {
      return `/${BASE_INTERNAL_URL}/asset-upload`;
    },
  },
  [InternalAdminRouteKeys.ActivityPreview]: {
    path: `activity-preview`,
    component: InternalAdminActivityPreview,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: () => {
      return `/${BASE_INTERNAL_URL}/activity-preview`;
    },
  },
  [InternalAdminRouteKeys.ContentBuilder]: {
    path: `content-builder`,
    component: InternalAdminContentBuilder,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: () => {
      return `/${BASE_INTERNAL_URL}/content-builder`;
    },
  },
  [InternalAdminRouteKeys.CreateEdetAccessCode]: {
    path: `classrooms/:classroomId/create-Edet-access-code`,
    component: CreateEdetAccessCode,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: ({ classroomId }: { classroomId: string }) => {
      return `/${BASE_INTERNAL_URL}/classrooms/${classroomId}/create-Edet-access-code`;
    },
  },
  [InternalAdminRouteKeys.Items]: {
    path: `items`,
    component: InternalAdminItems,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: () => {
      return `/${BASE_INTERNAL_URL}/items`;
    },
  },
  [InternalAdminRouteKeys.AiLessonGenerator]: {
    path: `ai-lesson-generator`,
    component: InternalAdminAiLessonGenerator,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: () => {
      return `/${BASE_INTERNAL_URL}/ai-lesson-generator`;
    },
  },
  [InternalAdminRouteKeys.LessonBlueprints]: {
    path: `lesson-blueprints`,
    component: InternalAdminLessonBlueprints,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: () => {
      return `/${BASE_INTERNAL_URL}/lesson-blueprints`;
    },
  },
  [InternalAdminRouteKeys.LessonBlueprintDetail]: {
    path: `lesson-blueprints/:blueprintId`,
    component: InternalAdminLessonBlueprintDetail,
    errorComponent: Error,
    layoutComponent: InternalAdminLayout,
    getRoute: ({ blueprintId }: { blueprintId: string }) => {
      return `/${BASE_INTERNAL_URL}/lesson-blueprints/${blueprintId}`;
    },
  },
} satisfies Record<InternalAdminRouteKeys, RouteData>;

export { internalAdminRoutes };
